// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import BarginBinQuickBuy from "@awc/App/Components/BargainBin/BarginBinQuickBuy";
import loadBargainBinProducts from "@awc/App/Handlers/BargainBin/GetBargainProducts";
import { CustomAppState } from "@awc/App/Store/Reducers";
import DataTable from "@awc/MobiusOverrides/DataTable/DataTable";
import LazyImage from "@awc/MobiusOverrides/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { ParentProductIdContext } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getComputedVariantProduct,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import changeQtyOrdered from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeQtyOrdered";
import changeUnitOfMeasure from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeUnitOfMeasure";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableBody, DataTableCell, DataTableRow, LoadingOverlay } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const selectedProductId = state.xBargain.selectedProductId;
    const productState = getProductStateByPath(state, selectedProductId);
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);

    return {
        bargain: state.xBargain.bargains,
        isProductLoading: state.xBargain.isProductLoading,
        product: computedVariantProduct.value,
        parentProductState: productState,
        selectedProductId,
        productInfo:
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined,
        isLoading: productState?.isLoading,
    };
};

const mapDispatchToProps = {
    loadBargainBinProducts,
    changeQtyOrdered,
    changeUnitOfMeasure,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BargainBinProductInfoStyles {
    container?: InjectableCss;
    dataCells?: DataTableCellProps;
    attributesTable?: DataTableProps;
    productInfo?: InjectableCss;
    barginBinQuickBuy?: InjectableCss;
    bottomSection?: InjectableCss;
}

export const bargainBinProductInfoStyles: BargainBinProductInfoStyles = {};

const styles = bargainBinProductInfoStyles;

const BargainBinProductInfo: React.FC<Props> = ({
    product,
    isProductLoading,
    selectedProductId,
    productInfo,
    isLoading,
    changeUnitOfMeasure,
    changeQtyOrdered,
    parentProductState,
}) => {
    // sort by label and name
    const sortedAttributes = useMemo(() => {
        if (!product?.attributeTypes?.length) {
            return [];
        }

        const clonedAttributes = structuredClone(product.attributeTypes);

        return clonedAttributes.sort((a: AttributeTypeModel, b: AttributeTypeModel) => {
            const aLabel = a.label || a.name;
            const bLabel = b.label || b.name;
            return aLabel.localeCompare(bLabel);
        });
    }, [product?.attributeTypes]);

    if (!product || !productInfo) {
        return (
            <StyledWrapper>
                <Typography variant="h1">{translate("Bargain Bin Deals")}</Typography>
                <Typography>{siteMessage("BargainBin_InstructionalMessage")}</Typography>
            </StyledWrapper>
        );
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: o => {
            changeQtyOrdered({
                qtyOrdered: o,
                productId: product.id,
            });
        },
        onUnitOfMeasureChanged: o => {
            changeUnitOfMeasure({
                unitOfMeasure: o,
                productId: product.id,
            });
        },
    };

    return (
        <ProductContext.Provider value={productContext}>
            <ParentProductIdContext.Provider value={parentProductState?.value?.id}>
                <LoadingOverlay loading={isProductLoading || isLoading}>
                    {product && (
                        <>
                            <StyledWrapper {...styles.productInfo}>
                                <LazyImage altText={product.imageAltText} src={product.mediumImagePath} />
                                <Typography variant="h2">
                                    {translate("Part Number: {0}", product.productNumber)}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.container}>
                                {createWidgetElement("ProductDetails/ProductDetailsBargain", {
                                    fields: {},
                                    isBargainBinPage: true,
                                } as any)}
                            </StyledWrapper>
                            <StyledWrapper {...styles.bottomSection}>
                                {(product.brand?.name || !!product.attributeTypes?.length) && (
                                    <DataTable {...styles.attributesTable} data-test-selector="BargainBin_TableHeader">
                                        <DataTableBody>
                                            {!!sortedAttributes &&
                                                sortedAttributes?.map((attributeType: AttributeTypeModel) => {
                                                    return (
                                                        <DataTableRow key={attributeType.id}>
                                                            <DataTableCell {...styles.dataCells}>
                                                                {attributeType.label || attributeType.name}
                                                            </DataTableCell>
                                                            <DataTableCell {...styles.dataCells}>
                                                                {[...(attributeType.attributeValues || [])]
                                                                    .sort((a, b) => {
                                                                        const orderCompare =
                                                                            (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
                                                                        return orderCompare !== 0
                                                                            ? orderCompare
                                                                            : a.valueDisplay.localeCompare(
                                                                                  b.valueDisplay,
                                                                              );
                                                                    })
                                                                    .map(attributeValue => attributeValue.valueDisplay)
                                                                    .join(", ")}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    );
                                                })}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                                <StyledWrapper {...styles.barginBinQuickBuy}>
                                    <BarginBinQuickBuy />
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    )}
                </LoadingOverlay>
            </ParentProductIdContext.Provider>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(BargainBinProductInfo);
